import { ApiError, buildQueryString, request } from "../client"

export type CustomerImportCompletenessStatus = "not_comparable" | "plausible" | "suspicious_drop"

export type CustomerImportCountComparison = {
  metric: string
  previous_count: number
  current_count: number
  missing_count: number
}

export type CustomerImportCompletenessAssessment = {
  status: CustomerImportCompletenessStatus
  baseline_draft_id?: string | null
  baseline_committed_at?: string | null
  comparisons: CustomerImportCountComparison[]
}

export type CustomerImportPreviewSummary = {
  record_count: number
  phone_number_count: number
  email_count: number
  property_count: number
  contact_count: number
  needs_review_count: number
  source_row_counts: Record<string, number>
  completeness: CustomerImportCompletenessAssessment
}

export type CustomerImportCommitResult = {
  ok: boolean
  review: number
  errors: number
  created: number
  reused: number
  linked: number
  skipped: number
  unit_resolution_counts: Record<string, number>
  contact_lifecycle_counts: Record<string, number>
  source_observation_counts?: Record<string, number>
  reimport_diff_counts?: Record<string, number>
  source_replacement_counts?: Record<string, number>
  warnings: string[]
  error_messages: string[]
}

export type CustomerImportDraft = {
  id: string
  organization_id: string
  project_id?: string | null
  portal_key: string
  source_file_name?: string | null
  source_mime_type?: string | null
  status: string
  preview_summary: CustomerImportPreviewSummary
  result_snapshot?: CustomerImportCommitResult | null
  created_by?: string | null
  expires_at: string
  committed_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type CustomerImportProgressResource = {
  type: string
  value: string
}

export type CustomerImportProgressStep = {
  id: string
  label: string
  status: string
  detail?: string | null
  resources: CustomerImportProgressResource[]
}

export type CustomerImportCommitStatus = {
  draft_id: string
  organization_id: string
  status: string
  phase: string
  batch_size: number
  total_records: number
  processed_records: number
  total_batches: number
  processed_batches: number
  steps: CustomerImportProgressStep[]
  result?: CustomerImportCommitResult | null
  message?: string | null
  started_at?: string | null
  updated_at?: string | null
  finished_at?: string | null
}

export type CustomerImportActiveStatus = {
  active: boolean
  draft?: CustomerImportDraft | null
  status?: CustomerImportCommitStatus | null
  timeout_at?: string | null
  message?: string | null
}

export type CustomerImportReviewItem = {
  id: string
  organization_id: string
  draft_id: string
  dedupe_key: string
  group_key: string
  risk_type: string
  severity: string
  status: string
  entity_refs: Record<string, unknown>
  source_record_key?: string | null
  source_summary?: string | null
  title: string
  summary: string
  recommended_action?: string | null
  available_actions: string[]
  payload: Record<string, unknown>
  resolved_at?: string | null
  created_at: string
  updated_at: string
}

export type CustomerImportReviewGroupSummary = {
  group_key: string
  label: string
  severity_max?: string | null
  total_count: number
  open_count: number
  resolved_count: number
  ignored_count: number
  auto_resolved_count: number
  examples: CustomerImportReviewItem[]
}

export type CustomerImportReviewSummary = {
  draft_id: string
  organization_id: string
  total_count: number
  total_open: number
  total_resolved: number
  total_ignored: number
  total_auto_resolved: number
  counts_by_severity: Record<string, number>
  counts_by_group: Record<string, number>
  groups: CustomerImportReviewGroupSummary[]
}

export type CustomerImportReviewItemsQuery = {
  groupKey?: string | null
  status?: string | null
  severity?: string | null
  riskType?: string | null
  search?: string | null
  limit?: number | null
  offset?: number | null
}

export type CustomerImportReviewItemListResponse = {
  items: CustomerImportReviewItem[]
  total: number
  limit: number
  offset: number
}

export type CustomerImportReviewAction = "ignore" | "mark_resolved" | "reopen"

export const fetchCustomerImportDraft = (draftId: string, organizationId?: string | null) =>
  request<CustomerImportDraft>(
    `/imports/customer-drafts/${encodeURIComponent(draftId)}${buildQueryString({
      organization_id: organizationId,
    })}`,
  )

export const fetchLatestCommittedCustomerImportDraft = async (
  organizationId?: string | null,
): Promise<CustomerImportDraft | null> => {
  try {
    return await request<CustomerImportDraft>(
      `/imports/customer-drafts/latest/committed${buildQueryString({
        organization_id: organizationId,
      })}`,
    )
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null

    throw error
  }
}

export const fetchActiveCustomerImportCommit = (organizationId?: string | null) =>
  request<CustomerImportActiveStatus>(
    `/imports/customer-drafts/active${buildQueryString({ organization_id: organizationId })}`,
  )

export const fetchCustomerImportReviewSummary = (draftId: string, organizationId?: string | null) =>
  request<CustomerImportReviewSummary>(
    `/imports/customer-drafts/${encodeURIComponent(draftId)}/review-summary${buildQueryString({
      organization_id: organizationId,
    })}`,
  )

export const fetchCustomerImportReviewItems = (
  draftId: string,
  query: CustomerImportReviewItemsQuery = {},
  organizationId?: string | null,
) => {
  const qs = buildQueryString({
    organization_id: organizationId,
    group_key: query.groupKey,
    status: query.status,
    severity: query.severity,
    risk_type: query.riskType,
    search: query.search,
    limit: query.limit,
    offset: query.offset,
  })

  return request<CustomerImportReviewItemListResponse>(
    `/imports/customer-drafts/${encodeURIComponent(draftId)}/review-items${qs}`,
  )
}

export const applyCustomerImportReviewItemAction = (
  draftId: string,
  itemId: string,
  action: CustomerImportReviewAction,
  organizationId?: string | null,
) =>
  request<CustomerImportReviewItem>(
    `/imports/customer-drafts/${encodeURIComponent(draftId)}/review-items/${encodeURIComponent(
      itemId,
    )}/action${buildQueryString({ organization_id: organizationId })}`,
    {
      method: "POST",
      body: JSON.stringify({ action }),
    },
  )
